import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { loadUserProgress } from '../components/progressManager';
import SmartObjectiveWelcome from './SmartObjectiveWelcome';
import userPreferencesManager from '../components/UserPreferencesManager';
import { 
  Search, Plus, Target, BookOpen, Sparkles, Compass, Zap,
  Briefcase, FileSpreadsheet, Star, AlertCircle, Lightbulb,
  Brain, Trophy, TrendingUp, Palmtree, Home, DollarSign, Heart,
  GraduationCap, Rocket, Loader2, Calendar, Layers, Trash2,
  CheckCircle, User, Settings, Cog, Info, Flag, ChevronDown
} from 'lucide-react';

// Configuration des priorités
const PRIORITY_CONFIG = {
  1: {
    label: 'P1 - Court terme',
    description: 'Important & Urgent - Forte valeur à court terme',
    color: 'red',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
    textColor: 'text-red-700',
    badgeBg: 'bg-red-500',
    icon: Zap
  },
  2: {
    label: 'P2 - Moyen terme',
    description: 'Important & Stratégique - Forte valeur à moyen terme',
    color: 'amber',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    textColor: 'text-amber-700',
    badgeBg: 'bg-amber-500',
    icon: Target
  },
  3: {
    label: 'P3 - Opportunité',
    description: 'Intéressant pour prospection',
    color: 'green',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
    textColor: 'text-green-700',
    badgeBg: 'bg-green-500',
    icon: Lightbulb
  }
};

// Modal de sélection de priorité
const PriorityModal = ({ isOpen, onClose, onSelect, currentPriority, objectiveName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Flag className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Définir la priorité</h3>
            <p className="text-sm text-gray-500 truncate">{objectiveName}</p>
          </div>
        </div>
        
        <div className="space-y-2 mb-6">
          {[1, 2, 3].map((priority) => {
            const config = PRIORITY_CONFIG[priority];
            const Icon = config.icon;
            const isSelected = currentPriority === priority;
            
            return (
              <button
                key={priority}
                onClick={() => onSelect(priority)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  isSelected 
                    ? `${config.borderColor} ${config.bgColor}` 
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.badgeBg} flex-shrink-0`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 mb-1">{config.label}</div>
                    <div className="text-sm text-gray-600">{config.description}</div>
                  </div>
                  {isSelected && (
                    <CheckCircle className={`w-5 h-5 ${config.textColor} flex-shrink-0`} />
                  )}
                </div>
              </button>
            );
          })}
          
          {/* Option pour retirer la priorité */}
          <button
            onClick={() => onSelect(null)}
            className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
              currentPriority === null 
                ? 'border-gray-400 bg-gray-50' 
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-400 flex-shrink-0">
                <Flag className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 mb-1">Sans priorité</div>
                <div className="text-sm text-gray-600">Retirer la priorité de cet objectif</div>
              </div>
              {currentPriority === null && (
                <CheckCircle className="w-5 h-5 text-gray-700 flex-shrink-0" />
              )}
            </div>
          </button>
        </div>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Annuler
        </button>
      </div>
    </div>
  );
};

// Modal de confirmation de suppression
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, objectiveName, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Supprimer l'objectif</h3>
            <p className="text-sm text-gray-500">Cette action est irréversible</p>
          </div>
        </div>
        
        <p className="text-gray-700 mb-6">
          Êtes-vous sûr de vouloir supprimer l'objectif "{objectiveName}" ? 
          Toutes les données associées (recommandations, plan d'action, etc.) seront définitivement perdues.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Supprimer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ObjectifsList = ({ userId, onSelectCompetence }) => {
  const [competences, setCompetences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [progressData, setProgressData] = useState({});
  const [error, setError] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [showRecent, setShowRecent] = useState(false);
  const [showActiveThisWeek, setShowActiveThisWeek] = useState(false); // ✅ CORRECTION 1: Désactivé par défaut
  const [showRecentDecisions, setShowRecentDecisions] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, objective: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedObjectiveForWelcome, setSelectedObjectiveForWelcome] = useState(null);
  const [priorityModal, setPriorityModal] = useState({ isOpen: false, objective: null });
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState(null);
  const [currentObjectiveId, setCurrentObjectiveId] = useState(null);

  const navigate = useNavigate();

  // Phases du processus
  const phases = [
    { 
      id: 'ambition', 
      label: 'Ambition', 
      color: 'from-purple-400 to-purple-600', 
      bgColor: 'bg-purple-50', 
      textColor: 'text-purple-700', 
      borderColor: 'border-purple-200',
      icon: <Target size={20} /> 
    },
    { 
      id: 'stratégie', 
      label: 'Stratégie', 
      color: 'from-blue-400 to-blue-600', 
      bgColor: 'bg-blue-50', 
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200', 
      icon: <Compass size={20} /> 
    },
    { 
      id: 'action', 
      label: 'Action', 
      color: 'from-green-400 to-green-600', 
      bgColor: 'bg-green-50', 
      textColor: 'text-green-700',
      borderColor: 'border-green-200', 
      icon: <Zap size={20} /> 
    }
  ];

  // Navigation vers HomePage avec objectif
  const navigateToHomePageWithObjective = async (objective, targetStep = 'strategy') => {
    if (onSelectCompetence) {
      onSelectCompetence(objective);
    }

    try {
      if (userId && objective) {
        await userPreferencesManager.setCurrentObjective(userId, {
          id: objective.id,
          title: objective.title || objective.name || '',
          theme: objective.theme || objective.domain || objective.chapitre || null,
          chapitre: objective.chapitre || null
        });
      }
    } catch (e) {
      console.error('ObjectifsList: erreur setCurrentObjective (navigate):', e);
    }
    
    navigate('/HomePage', { 
      state: { 
        competence: objective,
        targetStep: targetStep,
        fromObjectivesList: true
      }
    });
  };

  // Couleurs des domaines
  const getDomainColor = (domainName) => {
    switch(domainName) {
      case 'Professionnel': return { 
        bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', gradient: 'from-purple-400 to-purple-600',
        icon: <Briefcase className="w-7 h-7 text-purple-600" />
      };
      case 'Personnel': return { 
        bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', gradient: 'from-green-400 to-green-600',
        icon: <User className="w-7 h-7 text-green-600" />
      };
      case 'Educatif': return { 
        bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', gradient: 'from-blue-400 to-blue-600',
        icon: <GraduationCap className="w-7 h-7 text-blue-600" />
      };
      case 'Business': return { 
        bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', gradient: 'from-indigo-400 to-indigo-600',
        icon: <FileSpreadsheet className="w-7 h-7 text-indigo-600" />
      };
      case 'Développement entrepreneurial': return { 
        bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', gradient: 'from-orange-400 to-orange-600',
        icon: <Rocket className="w-7 h-7 text-orange-600" />
      };
      case 'Formation et apprentissage': return { 
        bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', gradient: 'from-indigo-400 to-indigo-600',
        icon: <BookOpen className="w-7 h-7 text-indigo-600" />
      };
      case 'Évolution de carrière': return { 
        bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', gradient: 'from-violet-400 to-violet-600',
        icon: <TrendingUp className="w-7 h-7 text-violet-600" />
      };
      case 'Santé et bien-être': return { 
        bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', gradient: 'from-emerald-400 to-emerald-600',
        icon: <Heart className="w-7 h-7 text-emerald-600" />
      };
      case 'Développement personnel': return { 
        bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', gradient: 'from-teal-400 to-teal-600',
        icon: <Brain className="w-7 h-7 text-teal-600" />
      };
      case 'Gestion immobilière': return { 
        bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', gradient: 'from-amber-400 to-amber-600',
        icon: <Home className="w-7 h-7 text-amber-600" />
      };
      case 'Finance et Banque': return { 
        bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', gradient: 'from-emerald-500 to-teal-600',
        icon: <DollarSign className="w-7 h-7 text-emerald-600" />
      };
      case 'Activités sportives': return { 
        bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', gradient: 'from-red-400 to-red-600',
        icon: <Zap className="w-7 h-7 text-red-600" />
      };
      case 'Voyages': return { 
        bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', gradient: 'from-pink-400 to-pink-600',
        icon: <Palmtree className="w-7 h-7 text-pink-600" />
      };
      case 'Technologie': return { 
        bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', gradient: 'from-cyan-400 to-cyan-600',
        icon: <Settings className="w-7 h-7 text-cyan-600" />
      };
      case 'Créatif': return { 
        bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', gradient: 'from-rose-400 to-rose-600',
        icon: <Sparkles className="w-7 h-7 text-rose-600" />
      };
      case 'Social': return { 
        bg: 'bg-lime-50', text: 'text-lime-700', border: 'border-lime-200', gradient: 'from-lime-400 to-lime-600',
        icon: <Heart className="w-7 h-7 text-lime-600" />
      };
      default: return { 
        bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', gradient: 'from-gray-400 to-gray-600',
        icon: <Compass className="w-7 h-7 text-gray-600" />
      };
    }
  };

  // Supprimer un objectif
  const handleDeleteObjective = async (objective) => {
    if (!objective || !userId) return;
    
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, 'users', userId, 'competences', objective.id));
      // Mettre à jour les préférences si l'objectif supprimé était l'objectif courant
      try {
        if (await userPreferencesManager.isCurrentObjective(userId, objective.id)) {
          await userPreferencesManager.clearCurrentObjective(userId);
        }
      } catch (e) {
        console.error('ObjectifsList: erreur clearCurrentObjective après suppression:', e);
      }
      setCompetences(prev => prev.filter(comp => comp.id !== objective.id));
      setDeleteModal({ isOpen: false, objective: null });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Impossible de supprimer cet objectif.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Changer la priorité d'un objectif
  const handleChangePriority = async (objective, newPriority) => {
    if (!objective || !userId) return;
    
    try {
      const objectiveRef = doc(db, 'users', userId, 'competences', objective.id);
      const priorityData = {
        priority: newPriority,
        priorityLabel: newPriority ? PRIORITY_CONFIG[newPriority].label : null,
        prioritySetAt: newPriority ? new Date() : null,
        updatedAt: new Date()
      };
      
      await updateDoc(objectiveRef, priorityData);
      
      // Mettre à jour l'état local
      setCompetences(prev => prev.map(obj => 
        obj.id === objective.id 
          ? { ...obj, ...priorityData }
          : obj
      ));
      
      setPriorityModal({ isOpen: false, objective: null });
    } catch (error) {
      console.error('Erreur lors du changement de priorité:', error);
      setError('Impossible de changer la priorité de cet objectif.');
    }
  };

  // Charger les compétences
  useEffect(() => {
    const fetchCompetences = async () => {
      if (!userId) {
        setError("Utilisateur non défini.");
        setLoading(false);
        return;
      }

      try {
        const competencesCollection = collection(db, 'users', userId, 'competences');
        const competencesSnapshot = await getDocs(competencesCollection);
        const competencesList = competencesSnapshot.docs.map(doc => {
          const data = doc.data();
          
          let realActionsCount = 0;
          let completedActionsCount = 0;
          
          if (data.actionPlan && data.actionPlan.steps && Array.isArray(data.actionPlan.steps)) {
            realActionsCount = data.actionPlan.steps.length;
            completedActionsCount = data.actionPlan.steps.filter(step => step.termine === true).length;
          } else if (data.recommendation && data.recommendation.actions && Array.isArray(data.recommendation.actions)) {
            realActionsCount = data.recommendation.actions.length;
            completedActionsCount = data.recommendation.actions.filter(action => action.completed === true).length;
          }
          
          const actionProgress = realActionsCount > 0 ? Math.round((completedActionsCount / realActionsCount) * 100) : 0;
          
          let phase = 'ambition';
          let phaseProgress = 0;
          
          if (data.recommendation) {
            phase = 'stratégie';
            phaseProgress = 20;
          }
          
          if (data.actionPlan || data.planExists) {
            phase = 'action';
            phaseProgress = 40;
          }
          
          return {
            id: doc.id,
            ...data,
            phase: phase,
            phaseProgress: phaseProgress,
            globalProgress: data.globalProgress || actionProgress || phaseProgress,
            shortTitle: data.shortTitle || data.title || '',
            name: data.shortTitle || data.title || '',
            type: data.type || 'project',
            domain: data.chapitre || 'Non classé',
            chapitre: data.chapitre || 'Non classé',
            actionsCount: realActionsCount,
            completedActionsCount: completedActionsCount,
            actionProgress: actionProgress,
            performance: data.globalProgress || actionProgress || phaseProgress,
            createdAt: data.createdAt || data.timestamp || new Date(),
            updatedAt: data.updatedAt || new Date(),
            hasStrategy: !!data.recommendation,
            hasActionPlan: !!(data.actionPlan || data.planExists),
            stepNotes: data.stepNotes || {},
            savedSources: data.savedSources || data.inspirationSources || [],
            priority: data.priority || null,
            priorityLabel: data.priorityLabel || null,
            prioritySetAt: data.prioritySetAt || null
          };
        });
        
        setCompetences(competencesList);
        const userProgress = await loadUserProgress(userId);
        setProgressData(userProgress);
        
      } catch (error) {
        setError("Erreur lors de la récupération des compétences : " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetences();
  }, [userId]);

  // Charger l'objectif courant depuis Firebase
  useEffect(() => {
    const fetchCurrentObjective = async () => {
      if (!userId) return;
      try {
        const current = await userPreferencesManager.getCurrentObjective(userId);
        setCurrentObjectiveId(current?.id || null);
      } catch (error) {
        console.error('ObjectifsList: erreur lors de la récupération de l\'objectif courant:', error);
      }
    };
    fetchCurrentObjective();
  }, [userId]);

  // Clic sur un objectif
  const handleObjectiveClick = async (objective) => {
    try {
      if (objective && userId) {
        await userPreferencesManager.setCurrentObjective(userId, {
          id: objective.id,
          title: objective.title || objective.name || '',
          theme: objective.theme || objective.domain || objective.chapitre || null,
          chapitre: objective.chapitre || null
        });
        // Mettre à jour immédiatement l'état local pour la mise en évidence
        setCurrentObjectiveId(objective.id);
      }
    } catch (e) {
      console.error('ObjectifsList: erreur setCurrentObjective:', e);
    }
    setSelectedObjectiveForWelcome(objective);
  };

  // Navigation depuis le modal
  const handleNavigateFromWelcome = async (targetStep) => {
    if (!selectedObjectiveForWelcome) return;

    try {
      if (userId) {
        await userPreferencesManager.updateLastAccessed(userId);
      }
    } catch (e) {
      console.error('ObjectifsList: erreur updateLastAccessed:', e);
    }

    if (targetStep === 'chat') {
      // Sauvegarder l'objectif avant de naviguer vers le chat
      try {
        if (userId && selectedObjectiveForWelcome) {
          await userPreferencesManager.setCurrentObjective(userId, {
            id: selectedObjectiveForWelcome.id,
            title: selectedObjectiveForWelcome.title || selectedObjectiveForWelcome.name || '',
            theme: selectedObjectiveForWelcome.theme || selectedObjectiveForWelcome.domain || selectedObjectiveForWelcome.chapitre || null,
            chapitre: selectedObjectiveForWelcome.chapitre || null
          });
        }
      } catch (e) {
        console.error('ObjectifsList: erreur setCurrentObjective (chat):', e);
      }

      // Notifier le composant parent App.js
      if (onSelectCompetence) {
        onSelectCompetence(selectedObjectiveForWelcome);
      }

      navigate(`/chat/${selectedObjectiveForWelcome.id}`);
      return;
    }

    navigateToHomePageWithObjective(selectedObjectiveForWelcome, targetStep);
  };

  // Projets récents (30 jours - CRÉÉS)
  const getRecentObjectives = (objectives) => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return objectives.filter(obj => {
      const createdDate = obj.createdAt ? new Date(obj.createdAt.seconds ? obj.createdAt.seconds * 1000 : obj.createdAt) : new Date();
      return createdDate >= thirtyDaysAgo;
    });
  };

  // Projets actifs cette semaine (7 jours - MODIFIÉS)
  const getActiveThisWeekObjectives = (objectives) => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return objectives.filter(obj => {
      // Vérifier updatedAt au niveau de l'objectif
      if (obj.updatedAt) {
        const updateDate = new Date(obj.updatedAt.seconds ? obj.updatedAt.seconds * 1000 : obj.updatedAt);
        if (updateDate >= sevenDaysAgo) {
          return true;
        }
      }
      
      // Vérifier aussi dans stepNotes
      if (obj.stepNotes) {
        return Object.values(obj.stepNotes).some(stepNote => {
          if (stepNote.updatedAt) {
            const updateDate = new Date(stepNote.updatedAt.seconds ? stepNote.updatedAt.seconds * 1000 : stepNote.updatedAt);
            return updateDate >= sevenDaysAgo;
          }
          return false;
        });
      }
      
      return false;
    });
  };

  // Projets avec décisions récentes (7 jours - DÉCISIONS DOCUMENTÉES)
  const getObjectivesWithRecentDecisions = (objectives) => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return objectives.filter(obj => {
      if (!obj.stepNotes) return false;
      
      // Vérifier si des décisions ont été ajoutées/modifiées cette semaine
      return Object.values(obj.stepNotes).some(stepNote => {
        if (!stepNote.decisions || !stepNote.decisions.trim()) return false;
        
        // Vérifier le timestamp de la note
        if (stepNote.updatedAt) {
          const updateDate = new Date(stepNote.updatedAt.seconds ? stepNote.updatedAt.seconds * 1000 : stepNote.updatedAt);
          return updateDate >= sevenDaysAgo;
        }
        
        // Si pas de timestamp, inclure quand même (décisions récentes potentielles)
        return true;
      });
    });
  };

  // Filtrer les objectifs
  const getFilteredObjectives = () => {
    let objectives = competences;

    objectives = objectives.filter(obj => {
      const matchesSearch = searchTerm === '' || 
        obj.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        obj.domain.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDomain = selectedDomain === 'all' || obj.domain === selectedDomain;
      
      if (selectedKPI === 'strategy-phase') {
        return obj.phase === 'stratégie' && matchesSearch && matchesDomain;
      }
      if (selectedKPI === 'transformation-phase') {
        return obj.phase === 'action' && matchesSearch && matchesDomain;
      }
      if (selectedKPI === 'high-performance') {
        return obj.performance >= 75 && matchesSearch && matchesDomain;
      }
      if (selectedKPI === 'actions-completed') {
        return obj.completedActionsCount > 0 && matchesSearch && matchesDomain;
      }
      
      return matchesSearch && matchesDomain;
    });

    // Filtres temporels exclusifs : actifs 7j OU décisions 7j OU récents 30j
    if (showRecentDecisions) {
      objectives = getObjectivesWithRecentDecisions(objectives);
    } else if (showActiveThisWeek) {
      objectives = getActiveThisWeekObjectives(objectives);
    } else if (showRecent) {
      objectives = getRecentObjectives(objectives);
    }

    // Tri par priorité : P1 > P2 > P3 > sans priorité
    objectives.sort((a, b) => {
      const priorityOrder = { 1: 1, 2: 2, 3: 3 };
      const priorityA = priorityOrder[a.priority || a.priorite] || 999;
      const priorityB = priorityOrder[b.priority || b.priorite] || 999;
      return priorityA - priorityB;
    });

    return objectives;
  };

  const filteredCompetences = getFilteredObjectives();

  // Domaines disponibles
  const getAvailableDomains = () => {
    const domainsWithObjectives = [...new Set(competences.map(comp => comp.chapitre).filter(Boolean))];
    return domainsWithObjectives.filter(domain => 
      competences.some(comp => comp.chapitre === domain)
    );
  };

  const availableDomains = getAvailableDomains();

  // Calcul des KPIs gamifiés + méthode Aladinn
  const calculateKPIs = () => {
    const domainFilteredObjectives = selectedDomain === 'all' 
      ? competences 
      : competences.filter(obj => obj.domain === selectedDomain);
    
    // 1. Projets en Stratégie
    const strategyCount = domainFilteredObjectives.filter(obj => obj.phase === 'stratégie').length;
    
    // 2. Projets en Action
    const actionCount = domainFilteredObjectives.filter(obj => obj.phase === 'action').length;
    
    // 3. Décisions cette semaine
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    let weeklyDecisions = 0;
    domainFilteredObjectives.forEach(objective => {
      if (objective.stepNotes) {
        Object.values(objective.stepNotes).forEach(stepNote => {
          // Vérifier si la note a été modifiée cette semaine
          let isRecent = false;
          if (stepNote.updatedAt) {
            const updateDate = new Date(stepNote.updatedAt.seconds ? stepNote.updatedAt.seconds * 1000 : stepNote.updatedAt);
            isRecent = updateDate >= sevenDaysAgo;
          }
          
          // Compter les décisions (si récentes ou si pas de timestamp)
          if (stepNote.decisions && stepNote.decisions.trim() && (isRecent || !stepNote.updatedAt)) {
            const sections = stepNote.decisions.split('\n\n').filter(s => s.trim());
            weeklyDecisions += sections.length;
          }
        });
      }
    });
    
    // 4. Projets actifs cette semaine
    const activeThisWeek = domainFilteredObjectives.filter(obj => {
      if (obj.updatedAt) {
        const updateDate = new Date(obj.updatedAt.seconds ? obj.updatedAt.seconds * 1000 : obj.updatedAt);
        return updateDate >= sevenDaysAgo;
      }
      // Vérifier aussi dans stepNotes
      if (obj.stepNotes) {
        return Object.values(obj.stepNotes).some(stepNote => {
          if (stepNote.updatedAt) {
            const updateDate = new Date(stepNote.updatedAt.seconds ? stepNote.updatedAt.seconds * 1000 : stepNote.updatedAt);
            return updateDate >= sevenDaysAgo;
          }
          return false;
        });
      }
      return false;
    }).length;

    return {
      strategyCount,
      actionCount,
      weeklyDecisions,
      activeThisWeek
    };
  };

  const kpis = calculateKPIs();

  // ✅ CORRECTION 3: Gestion améliorée des filtres - désactiver "Objectifs actifs" si un autre KPI est sélectionné
  const handleFilterChange = (key, value) => {
    if (key === 'search') {
      setSearchTerm(value);
    } else if (key === 'domain') {
      setSelectedDomain(value);
    } else if (key === 'showRecent') {
      setShowRecent(value);
      if (value) {
        setShowActiveThisWeek(false);
        setShowRecentDecisions(false);
        setSelectedKPI(null); // Désactiver les autres KPIs
      }
    } else if (key === 'showActiveThisWeek') {
      setShowActiveThisWeek(value);
      if (value) {
        setShowRecent(false);
        setShowRecentDecisions(false);
        setSelectedKPI(null); // Désactiver les autres KPIs
      }
    } else if (key === 'showRecentDecisions') {
      setShowRecentDecisions(value);
      if (value) {
        setShowRecent(false);
        setShowActiveThisWeek(false);
        setSelectedKPI(null); // Désactiver les autres KPIs
      }
    }
    
    // Si un KPI numérique est sélectionné, désactiver les filtres temporels
    if (key === 'kpi') {
      setSelectedKPI(value);
      if (value) {
        setShowRecent(false);
        setShowActiveThisWeek(false);
        setShowRecentDecisions(false);
      }
    }
  };

  const handleCreateNewObjective = async () => {
    if (onSelectCompetence) {
      onSelectCompetence(null);
    }

    try {
      if (userId) {
        await userPreferencesManager.clearCurrentObjective(userId);
      }
    } catch (e) {
      console.error('ObjectifsList: erreur clearCurrentObjective (new objective):', e);
    }
    
    navigate('/HomePage', { 
      state: { 
        targetStep: 'ambition',
        fromObjectivesList: true,
        clearCurrentObjective: true
      }
    });
  };
  
  // Composant carte d'objectif
  const ObjectiveListItem = ({ objective, onClick, isActive }) => {
    const phase = objective.phase || 'ambition';
    const phaseObj = phases.find(p => p.id === phase) || phases[0];
    const progress = objective.performance || 0;
    const domainColors = getDomainColor(objective.chapitre || 'default');

    // Fonction pour formater la date
    const formatDate = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date.seconds ? date.seconds * 1000 : date);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Utiliser updatedAt ou createdAt
    const displayDate = objective.updatedAt || objective.createdAt;

    return (
      <div className={`rounded-lg p-4 cursor-pointer transition-all duration-200 ${
        isActive
          ? 'bg-blue-50 border-l-4 border-l-blue-600 border border-blue-300 shadow-md'
          : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}>
        {/* Version desktop */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Colonne 1: Objectif (45%) */}
          <div className="w-[45%] flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg ${domainColors.bg} flex-shrink-0`}>
              {React.cloneElement(domainColors.icon, { className: "w-5 h-5" })}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3
                  className={`${isActive ? 'font-semibold' : 'font-medium'} text-gray-800 truncate hover:text-blue-600 transition-colors flex-1 cursor-pointer`}
                  title={objective.name}
                  onClick={() => onClick(objective)}
                >
                  {objective.name}
                </h3>
                {isActive && (
                  <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Actif
                  </span>
                )}
                {(objective.priority || objective.priorite) && 
                 typeof (objective.priority || objective.priorite) === 'number' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPriorityModal({ isOpen: true, objective });
                    }}
                    className={`ml-2 text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity self-center ${
                      (objective.priority === 1 || objective.priorite === 1) ? 'bg-red-500 text-white' :
                      (objective.priority === 2 || objective.priorite === 2) ? 'bg-orange-500 text-white' :
                      'bg-blue-500 text-white'
                    }`}
                    title="Modifier la priorité"
                  >
                    P{objective.priority || objective.priorite}
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${phaseObj.color}`}></span>
                  {phaseObj.label}
                </span>
                <span className="truncate">{objective.chapitre || 'Non classé'}</span>
              </div>
            </div>
          </div>

          {/* Colonne 2: Tâches (10%) - Cliquable vers Plan d'action */}
          <div 
            className="w-[10%] cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigateToHomePageWithObjective(objective, 'action');
            }}
            title="Voir le plan d'action"
          >
            <div className="text-sm font-medium text-gray-700">
              {objective.completedActionsCount}/{objective.actionsCount}
            </div>
          </div>

          {/* Colonne 3: Modifié le (10%) - Cliquable vers Stratégie */}
          <div 
            className="w-[10%] cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigateToHomePageWithObjective(objective, 'strategy');
            }}
            title="Voir la stratégie"
          >
            <div className="text-xs text-gray-600">
              {formatDate(displayDate)}
            </div>
          </div>

          {/* Colonne 4: Progression (15%) - Cliquable vers Stratégie */}
          <div 
            className="w-[15%] cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigateToHomePageWithObjective(objective, 'strategy');
            }}
            title="Voir la stratégie"
          >
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r transition-all duration-500 ${phaseObj.color}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs font-medium text-gray-700 min-w-[35px]">{progress}%</span>
            </div>
          </div>
          
          {/* Colonne 5: Actions (20%) - 4 boutons avec flag en premier */}
          <div className="w-[20%]">
            <div className="flex gap-2 justify-end">
              {/* 1. Flag priorité */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setPriorityModal({ isOpen: true, objective });
                }}
                className="p-2 hover:bg-orange-50 rounded transition-colors text-orange-500 hover:text-orange-600"
                title={objective.priority ? `Priorité: P${objective.priority}` : "Définir une priorité"}
              >
                🚩
              </button>

              {/* 2. Énergie - Navigue vers Plan d'action */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToHomePageWithObjective(objective, 'action');
                }}
                className="p-2 hover:bg-yellow-50 rounded transition-colors"
                title="Voir le plan d'action"
              >
                ⚡
              </button>

              {/* 3. Modifier - Navigue vers Stratégie */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToHomePageWithObjective(objective, 'strategy');
                }}
                className="p-2 hover:bg-blue-50 rounded transition-colors text-blue-600"
                title="Voir la stratégie"
              >
                ✏️
              </button>
              
              {/* 4. Supprimer */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteModal({ isOpen: true, objective });
                }}
                className="p-2 hover:bg-red-50 rounded transition-colors text-red-600"
                title="Supprimer l'objectif"
              >
                🗑️
              </button>
            </div>
          </div>
        </div>

        {/* Version mobile - Simplifiée */}
        <div className="lg:hidden">
          <div className="flex items-start gap-3 mb-3">
            <div className={`p-2 rounded-lg ${domainColors.bg} flex-shrink-0`}>
              {React.cloneElement(domainColors.icon, { className: "w-5 h-5" })}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3
                  className={`${isActive ? 'font-semibold' : 'font-medium'} text-gray-800 line-clamp-2 flex-1`}
                  onClick={() => onClick(objective)}
                >
                  {objective.name}
                </h3>
                {isActive && (
                  <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Actif
                  </span>
                )}
              </div>
              
              {/* P1, Type et Phase sur une seule ligne */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {(objective.priority || objective.priorite) && 
                 typeof (objective.priority || objective.priorite) === 'number' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPriorityModal({ isOpen: true, objective });
                    }}
                    className={`text-xs font-bold px-2 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                      (objective.priority === 1 || objective.priorite === 1) ? 'bg-red-500 text-white' :
                      (objective.priority === 2 || objective.priorite === 2) ? 'bg-orange-500 text-white' :
                      'bg-blue-500 text-white'
                    }`}
                    title="Modifier la priorité"
                  >
                    P{objective.priority || objective.priorite}
                  </button>
                )}
                
                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                  {objective.chapitre || 'Non classé'}
                </span>
                
                <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${phaseObj.bgColor} ${phaseObj.textColor}`}>
                  <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${phaseObj.color}`}></span>
                  {phaseObj.label}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Icône de modification centrée - Accès direct à la stratégie */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateToHomePageWithObjective(objective, 'strategy');
              }}
              className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1.5"
              title="Accéder au parcours"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="text-xs font-medium">Modifier</span>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setPriorityModal({ isOpen: true, objective });
              }}
              className="px-3 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors"
            >
              🚩
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteModal({ isOpen: true, objective });
              }}
              className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
              title="Supprimer l'objectif"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Titre dynamique
  const getDynamicTitle = () => {
    if (selectedKPI === 'high-performance') {
      return 'Objectifs réussis (≥75%)';
    }
    if (selectedKPI === 'strategy-phase') {
      return 'Projets en phase stratégie';
    }
    if (selectedKPI === 'transformation-phase') {
      return 'Projets en action';
    }
    if (selectedKPI === 'actions-completed') {
      return 'Projets avec actions réalisées';
    }
    if (showRecentDecisions) {
      return 'Projets avec décisions récentes';
    }
    if (showActiveThisWeek) {
      return 'Projets actifs (7 jours)';
    }
    if (showRecent) {
      return 'Projets récents (30 jours)';
    }
    if (selectedDomain !== 'all') {
      return `${selectedDomain}`;
    }
    return 'Tous vos objectifs';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="ml-3 text-lg text-blue-600">Chargement de vos objectifs...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header ultra-compact avec titre dynamique intégré */}
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-b-xl shadow-sm border-b border-gray-200 p-3 lg:p-4 mb-3">
        <div className="max-w-[2000px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 flex-1">
              <Target className="w-5 h-5 text-indigo-600 flex-shrink-0" />
              <h1 className="text-base lg:text-lg font-bold text-gray-800 truncate">
                {getDynamicTitle()}
              </h1>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {/* Ligne 1 mobile: Champ de recherche pleine largeur */}
              <div className="relative flex-grow sm:flex-grow-0 sm:w-80">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-gray-200 bg-white/90 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                />
              </div>

              {/* Ligne 2 mobile: Domaine + Bouton Nouveau */}
              <div className="flex gap-2">
                <select
                  value={selectedDomain}
                  onChange={(e) => handleFilterChange('domain', e.target.value)}
                  className="rounded-lg border border-gray-200 px-2 sm:px-3 py-2.5 bg-white/90 text-gray-600 focus:ring-2 focus:ring-blue-500 transition-all text-sm flex-grow"
                >
                  <option value="all">Tous</option>
                  {availableDomains.map(domain => (
                    <option key={domain} value={domain}>{domain}</option>
                  ))}
                </select>

                <button
                  onClick={handleCreateNewObjective}
                  className="flex items-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-all shadow-sm text-sm font-medium flex-shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nouveau</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[2000px] mx-auto px-4 py-1 space-y-4 lg:space-y-6">
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p className="text-sm lg:text-base">{error}</p>
            </div>
          </div>
        )}

        {/* KPIs - Sélection de projets */}
        <div className="grid grid-cols-3 gap-2 lg:gap-6 mb-4 lg:mb-8">
          
          {/* KPI 1: Projets en Stratégie */}
          <div 
            className={`bg-white rounded-lg lg:rounded-2xl shadow-lg border-2 p-2 lg:p-6 cursor-pointer transition-all duration-200 hover:shadow-xl relative ${
              selectedKPI === 'strategy-phase' 
                ? 'border-blue-300 bg-blue-50 shadow-blue-100' 
                : 'border-gray-100 hover:border-blue-200'
            }`}
            onClick={() => handleFilterChange('kpi', selectedKPI === 'strategy-phase' ? null : 'strategy-phase')}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-1.5 lg:gap-3">
                  <div className="w-7 h-7 lg:w-12 lg:h-12 bg-blue-50 rounded-md lg:rounded-xl flex items-center justify-center shadow-sm">
                    <Compass className="w-3.5 h-3.5 lg:w-6 lg:h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[10px] lg:text-sm font-semibold text-gray-700 leading-tight">Projets en Stratégie</h3>
                    <p className="text-base lg:text-2xl font-bold text-gray-900">{kpis.strategyCount}</p>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block text-xs text-gray-500 lg:text-right">
                <span className="lg:block">Prêts pour</span>
                <span className="lg:block">l'action</span>
              </div>
            </div>
            {selectedKPI === 'strategy-phase' && (
              <div className="absolute -top-1 left-1 right-1 lg:left-4 lg:right-4 h-0.5 lg:h-2 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"></div>
            )}
          </div>

          {/* KPI 2: Projets en Action */}
          <div 
            className={`bg-white rounded-lg lg:rounded-2xl shadow-lg border-2 p-2 lg:p-6 cursor-pointer transition-all duration-200 hover:shadow-xl relative ${
              selectedKPI === 'transformation-phase' 
                ? 'border-green-300 bg-green-50 shadow-green-100' 
                : 'border-gray-100 hover:border-green-200'
            }`}
            onClick={() => handleFilterChange('kpi', selectedKPI === 'transformation-phase' ? null : 'transformation-phase')}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-1.5 lg:gap-3">
                  <div className="w-7 h-7 lg:w-12 lg:h-12 bg-green-50 rounded-md lg:rounded-xl flex items-center justify-center shadow-sm">
                    <Zap className="w-3.5 h-3.5 lg:w-6 lg:h-6 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[10px] lg:text-sm font-semibold text-gray-700 leading-tight">Projets en Action</h3>
                    <p className="text-base lg:text-2xl font-bold text-gray-900">{kpis.actionCount}</p>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block text-xs text-gray-500 lg:text-right">
                <span className="lg:block">Plans d'action</span>
                <span className="lg:block">actifs</span>
              </div>
            </div>
            {selectedKPI === 'transformation-phase' && (
              <div className="absolute -top-1 left-1 right-1 lg:left-4 lg:right-4 h-0.5 lg:h-2 bg-gradient-to-r from-green-400 to-green-600 rounded-full"></div>
            )}
          </div>

          {/* KPI 3: Décisions cette semaine */}
          <div 
            className={`bg-white rounded-lg lg:rounded-2xl shadow-lg border-2 p-2 lg:p-6 cursor-pointer transition-all duration-200 hover:shadow-xl relative ${
              showRecentDecisions 
                ? 'border-orange-300 bg-orange-50 shadow-orange-100' 
                : 'border-gray-100 hover:border-orange-200'
            }`}
            onClick={() => handleFilterChange('showRecentDecisions', !showRecentDecisions)}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-1.5 lg:gap-3">
                  <div className="w-7 h-7 lg:w-12 lg:h-12 bg-orange-50 rounded-md lg:rounded-xl flex items-center justify-center shadow-sm">
                    <Target className="w-3.5 h-3.5 lg:w-6 lg:h-6 text-orange-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[10px] lg:text-sm font-semibold text-gray-700 leading-tight">Décisions</h3>
                    <p className="text-base lg:text-2xl font-bold text-gray-900">{kpis.weeklyDecisions}</p>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block text-xs text-gray-500 lg:text-right">
                <span className="lg:block">Actions</span>
                <span className="lg:block">engagées</span>
              </div>
            </div>
            {showRecentDecisions && (
              <div className="absolute -top-1 left-1 right-1 lg:left-4 lg:right-4 h-0.5 lg:h-2 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full"></div>
            )}
          </div>
        </div>

        {/* Liste des objectifs */}
        <div className="space-y-4">
          {filteredCompetences.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-6 lg:p-8 text-center">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Target className="h-6 w-6 lg:h-8 lg:w-8 text-gray-400" />
                </div>
                <h3 className="text-base lg:text-lg font-medium text-gray-800">Aucun objectif trouvé</h3>
                <p className="text-sm lg:text-base text-gray-600 max-w-md">
                  {searchTerm || selectedDomain !== 'all' || selectedKPI || showRecent || showActiveThisWeek || showRecentDecisions
                    ? "Aucun objectif ne correspond à vos critères. Essayez de modifier vos filtres."
                    : "Commencez par créer votre premier objectif."}
                </p>
                
                {!searchTerm && selectedDomain === 'all' && !selectedKPI && !showRecent && !showActiveThisWeek && !showRecentDecisions && (
                  <button
                    onClick={handleCreateNewObjective}
                    className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm lg:text-base"
                  >
                    <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
                    Créer un objectif
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 hidden lg:block">
                <div className="flex items-center gap-4 px-4 py-3 text-sm font-medium text-gray-600">
                  <div className="w-[45%]">Objectif</div>
                  <div className="w-[10%]">Tâches</div>
                  <div className="w-[10%]">Modifié le</div>
                  <div className="w-[15%]">Progression</div>
                  <div className="w-[20%]">Actions</div>
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-1 p-2">
                  {filteredCompetences.map((objective) => (
                    <ObjectiveListItem
                      key={objective.id}
                      objective={objective}
                      onClick={handleObjectiveClick}
                      isActive={currentObjectiveId === objective.id}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Insights */}
        {filteredCompetences.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-4 lg:p-6">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <Target className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm lg:text-base font-medium text-gray-800 mb-2">Vue d'ensemble - Méthode Aladinn</h3>
                  <div className="text-xs lg:text-sm text-gray-600 space-y-1 lg:space-y-2">
                    <p>• <strong>{kpis.strategyCount}</strong> projets en phase Stratégie (prêts pour l'action)</p>
                    <p>• <strong>{kpis.actionCount}</strong> projets en phase Action (transformation active)</p>
                    <p>• <strong>{kpis.weeklyDecisions}</strong> décisions prises cette semaine</p>
                    <p>• <strong>{kpis.activeThisWeek}</strong> projets actifs ces 7 derniers jours</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-4 lg:p-6">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <Lightbulb className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-sm lg:text-base font-medium text-gray-800 mb-2">Actions recommandées</h3>
                  <div className="text-xs lg:text-sm text-gray-600 space-y-1 lg:space-y-2">
                    <p>• Commencez par les objectifs les plus en retard</p>
                    <p>• Développez vos stratégies avant de passer à l'action</p>
                    <p>• Suivez régulièrement l'avancement de vos transformations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Conseils d'utilisation - Mis à jour */}
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
              <Lightbulb className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm lg:text-base font-medium text-gray-800 mb-1">Conseil d'utilisation</h3>
              <div className="text-xs lg:text-sm text-gray-600 space-y-1 lg:space-y-2">
                <p>
                  <strong>📌 Filtrage flexible :</strong> Par défaut, tous vos objectifs sont affichés. Cliquez sur les KPIs en haut pour filtrer selon vos besoins (objectifs actifs, en stratégie, en action, ou avec décisions récentes).
                </p>
                <p>
                  <strong>🎯 Navigation rapide :</strong> Cliquez sur un objectif pour voir son accueil personnalisé avec recommandations d'actions basées sur votre avancement réel.
                </p>
                <p>
                  Les boutons "Stratégie" et "Plan d'action" vous mènent directement au parcours Aladinn. Le titre change selon vos filtres actifs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de suppression */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, objective: null })}
        onConfirm={() => handleDeleteObjective(deleteModal.objective)}
        objectiveName={deleteModal.objective?.name || ''}
        isDeleting={isDeleting}
      />

      {/* Modal d'accueil intelligent */}
      {selectedObjectiveForWelcome && (
        <SmartObjectiveWelcome
          objective={selectedObjectiveForWelcome}
          onNavigate={handleNavigateFromWelcome}
          onClose={() => setSelectedObjectiveForWelcome(null)}
        />
      )}

      {/* Modal de priorité */}
      <PriorityModal
        isOpen={priorityModal.isOpen}
        onClose={() => setPriorityModal({ isOpen: false, objective: null })}
        onSelect={(priority) => handleChangePriority(priorityModal.objective, priority)}
        currentPriority={priorityModal.objective?.priority || null}
        objectiveName={priorityModal.objective?.name || ''}
      />
    </div>
  );
};

export default ObjectifsList;
